import styled from "styled-components";
import { useIntl, FormattedMessage } from "react-intl";
import { Button, TabContainer, TabItem, OptionsList, Footer, SecondaryCtaButton, OptionItem, QuaternaryCtaButton, CustomWrapOption, Modal, Icon, InvertTheme, PrimaryCtaButton, QuinaryCtaButton, TertiaryCtaButton, BottomPanelState, OptionItemProps } from "slate-ui-components";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { VisualizerSlice, optionToggle, optionClick, removeCustomWrap, deselectOptions, findOption } from "./store/reducers/visualizer";
import { ImageMedia, OptionData } from "./types/VisualizerTypes";
import { useItemDetailsDialog } from "./Dialogs/ItemDetailDialog";
import { useCustomColourPanel } from "./CustomColourPanel";

import { getInitialUrlArgs } from "./UrlArgsProvider";
import { useImageMedia } from "./utils/useImageMedia";
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { JSONEquality } from "./utils/equal";
import { useAnalytics } from "./AnalyticsProvider";

import SilhouetteImage from "./assets/silhouette.png"
import { Is3DAllowed } from "./components/Visualizer";
import { LoadingOverlay } from "./LoadingOverlay";


const ShrinkText = styled.span`
    text-overflow: ellipsis;
    flex-shrink: 1;
    overflow: hidden;
    flex-basis: 100%;
`

const PlaceholderMessage = styled.span`
    //color: ${({theme}) => theme.colours.text.secondary};
    ${p => p.theme.fonts.body1.normal}
`

const FooterCTA = styled(Button)`
    ${p => p.theme.fonts.body1.normal}
    color: ${({ theme }) => theme.colours.text.primary};
    --icon-foreground: ${({ theme }) => theme.colours.text.primary};
`

const autoItemApply = getInitialUrlArgs().itemsAutoApply;

const SecondLevelOptionsStyle = styled.div<{$show: boolean}>`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
    background-color: #262626;
    border-radius: 10px 10px 0px 0px;
    pointer-events: auto;
    display: flex;
    flex-direction: column;
    padding: 0px 0px;
    padding-top: 31px;
    box-sizing: border-box;

    max-width: 1286px;
    box-sizing: border-box;

    ${FooterCTA}{
        padding: 0px 20px;
    }
    & > button#close {
        position: absolute;
        right: 10px;
        top: 10px;
    }

    transition: transform 0.3s ease-in-out;

    transform: ${(p) => p.$show ? "translateY(0)" : "translateY(100%)"};

    &>:last-child{
        border-top: #535353 1px solid;
    }
`

const Space = styled.div`
    flex-grow: 1;
    flex-basis: 0;
`

const ButtonsGroup = styled.div`
    display: flex;
    gap: 10px;
`

const CloseButton = styled(Button)`
    margin: -20px;
    padding: 20px;
`

const IconButton = styled(QuaternaryCtaButton)`
    min-width: unset;
    padding: 20px;
`

const CloseSecondaryMenuOnFocusLost = false

export function ConfigurationTab(props: { configuratorStateFunc: Dispatch<SetStateAction<BottomPanelState>>, panelState: BottomPanelState, onCollapse: () => void, onExpand: () => void })
{    
    const {record} = useAnalytics()
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const [showVariants, setShowVariants] = useState<string | undefined>(undefined);
    const categories = useAppSelector(s => s.configuration.currentVisualizerConfig.categories);

    const category = useAppSelector(s => s.configuration.currentVisualizerConfig.categories.find(category => category.id == s.configuration.activeCategory)!);
    const categoryIndex = useAppSelector(s => s.configuration.currentVisualizerConfig.categories.findIndex(category => category.id == s.configuration.activeCategory));
    const group = useAppSelector(s => s.configuration.optionGroups[category.group], JSONEquality);

    const focusedItem = useAppSelector(s => s.configuration.focusedOption, JSONEquality);
    const focusedOption = useAppSelector(s => s.configuration.focusedOption && s.configuration.optionGroups[s.configuration.focusedOption?.optionGroup].options[s.configuration.focusedOption?.optionId], JSONEquality);
    const focusedItemSelected = useAppSelector(s => focusedItem && s.configuration.selectedOptions[focusedItem?.optionGroup].includes(focusedItem?.optionId));
    const selection = useAppSelector(s => s.configuration.selectedOptions);

    const itemDetailsDialog = useItemDetailsDialog(focusedItem?.optionGroup, focusedItem?.optionId, "footer")
    const optionsSubcategories = useGroupedOptions(category.group, group?.options);
    const toggleItem = () => focusedItem && dispatch(optionToggle({ group: focusedItem.optionGroup, option: focusedItem.optionId }));
    const focusedSubcategory = optionsSubcategories.find(g => showVariants === g.id) ?? optionsSubcategories.find(g =>  g.topItem[0] == focusedItem?.optionId || g.items.some(i => focusedItem?.optionId == i[0]))

    useEffect(() =>
    {
        if (props.panelState != "collapsed")
            setShowVariants(undefined);
    },[props.panelState])

    const removeWrap = () => {
        record?.(["Configurator", "DeleteCustomWrap"])
        if(focusedItem) {
            dispatch(removeCustomWrap(focusedItem.optionId))
        }
    }

    const editWrap = () =>
    {
        record?.(["Configurator", "EditCustomWrap"])
        if(focusedItem) {
            dispatch(VisualizerSlice.actions.editWrapRequest())
        }
    }

    const {onCollapse, onExpand} = props
    const onShowVariants = useCallback((id?: string) => {
        if (id)
            onCollapse();
        else
            onExpand();
            
        setShowVariants(id);
    }, [onCollapse, onExpand])

    const secondaryLevelMenuRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
		if(!showVariants || !CloseSecondaryMenuOnFocusLost) {
			return;
		}

		const onPointerDown = (ev: PointerEvent) => {
			const rect = secondaryLevelMenuRef.current?.getBoundingClientRect()
			if(rect) {
				if(ev.clientY > rect.bottom || ev.clientY < rect.top || ev.clientX < rect.left || ev.clientX > rect.right) {
					onShowVariants(undefined)
				}
			}
		}

		window.addEventListener("pointerdown", onPointerDown, {capture: true})
		return () => {
			window.removeEventListener("pointerdown", onPointerDown, {capture: true})
		}
	}, [onShowVariants, showVariants])

    const onTabChangeFunc = (id:string) => {
        if(id == "Summary"){
            if(props.configuratorStateFunc){
                props.configuratorStateFunc("expanded")
            }
        }
        else{
            dispatch(VisualizerSlice.actions.setActiveCategory(id))
        }
    }

    const modesReady = useAppSelector(s => s.configuration.modesReady)

    const noneOptions:Record<string, ImageMedia | undefined> = {
        "tire": { type: "image", url: "data:image/svg+xml,%3Csvg width='90' height='90' viewBox='0 0 90 90' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='90' height='90' rx='0' fill='%23262626'/%3E%3Cg clip-path='url(%23clip0_3419_2911)'%3E%3Cpath d='M45 14C62.1208 14 76 27.8792 76 45C76 62.1208 62.1208 76 45 76C27.8792 76 14 62.1208 14 45C14 27.8792 27.8792 14 45 14Z' stroke='%23F9F9F9' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M45 27C54.9411 27 63 35.0589 63 45C63 54.9411 54.9411 63 45 63C35.0589 63 27 54.9411 27 45C27 35.0589 35.0589 27 45 27Z' stroke='%23F9F9F9' stroke-width='2.11765' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M66.9203 24.4936L67.6274 23.7864L66.2132 22.3722L65.5061 23.0793L66.9203 24.4936ZM62.7917 25.7937C62.4012 26.1842 62.4012 26.8174 62.7917 27.2079C63.1822 27.5985 63.8154 27.5985 64.2059 27.2079L62.7917 25.7937ZM23.0797 65.5057L22.3726 66.2129L23.7868 67.6271L24.4939 66.92L23.0797 65.5057ZM27.2083 64.2056C27.5988 63.8151 27.5988 63.1819 27.2083 62.7914C26.8178 62.4008 26.1846 62.4008 25.7941 62.7914L27.2083 64.2056ZM65.5061 66.92L66.2132 67.6271L67.6274 66.2129L66.9203 65.5057L65.5061 66.92ZM64.2059 62.7914C63.8154 62.4008 63.1822 62.4008 62.7917 62.7914C62.4012 63.1819 62.4012 63.8151 62.7917 64.2056L64.2059 62.7914ZM24.4939 23.0793L23.7868 22.3722L22.3726 23.7864L23.0797 24.4936L24.4939 23.0793ZM25.7941 27.2079C26.1846 27.5985 26.8178 27.5985 27.2083 27.2079C27.5988 26.8174 27.5988 26.1842 27.2083 25.7937L25.7941 27.2079ZM75 45.9996L76 45.9996L76 43.9996L75 43.9996L75 45.9996ZM71.1613 43.9996C70.609 43.9996 70.1613 44.4474 70.1613 44.9996C70.1613 45.5519 70.609 45.9996 71.1613 45.9996L71.1613 43.9996ZM15 43.9996L14 43.9996L14 45.9996L15 45.9996L15 43.9996ZM18.8387 45.9996C19.391 45.9996 19.8387 45.5519 19.8387 44.9996C19.8387 44.4474 19.391 43.9996 18.8387 43.9996L18.8387 45.9996ZM44 74.9996V75.9996H46V74.9996H44ZM46 71.1609C46 70.6087 45.5523 70.1609 45 70.1609C44.4477 70.1609 44 70.6087 44 71.1609H46ZM46 14.9996L46 13.9996H44L44 14.9996H46ZM44 18.8384C44 19.3906 44.4477 19.8384 45 19.8384C45.5523 19.8384 46 19.3906 46 18.8384H44ZM66.2132 23.7864L65.5061 23.0793L62.7917 25.7937L63.4988 26.5008L64.2059 27.2079L66.9203 24.4936L66.2132 23.7864ZM23.7868 66.2129L24.4939 66.92L27.2083 64.2056L26.5012 63.4985L25.7941 62.7914L23.0797 65.5057L23.7868 66.2129ZM66.2132 66.2129L66.9203 65.5057L64.2059 62.7914L63.4988 63.4985L62.7917 64.2056L65.5061 66.92L66.2132 66.2129ZM23.7868 23.7864L23.0797 24.4936L25.7941 27.2079L26.5012 26.5008L27.2083 25.7937L24.4939 23.0793L23.7868 23.7864ZM75 44.9996L75 43.9996L71.1613 43.9996L71.1613 44.9996L71.1613 45.9996L75 45.9996L75 44.9996ZM15 44.9996L15 45.9996L18.8387 45.9996L18.8387 44.9996L18.8387 43.9996L15 43.9996L15 44.9996ZM45 74.9996H46V71.1609H45H44V74.9996H45ZM45 14.9996H44V18.8384H45H46V14.9996H45Z' fill='%23F9F9F9'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0_3419_2911'%3E%3Crect width='76' height='76' fill='white' transform='matrix(1 0 0 -1 7 83)'/%3E%3C/clipPath%3E%3CclipPath id='clip1_3419_2911'%3E%3Crect x='65' y='3' width='22' height='22' rx='11' fill='white'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E%0A" }
    }

    return <>
        <TabContainer tabIndex={categoryIndex} onTabChange={onTabChangeFunc}>
            {categories.map(s => <TabItem key={s.id} id={s.id} label={intl.formatMessage(s.displayName)} testId={s.id} />)}
            {categories.length > 0 &&  Object.values(modesReady).some(v => v) &&
            <TabItem key="Summary" id="Summary" label={intl.formatMessage({id: "mode.summary", defaultMessage: "Summary"})} testId={"tab.Summary"} />            
            }
        </TabContainer>
        <OptionsList key={category?.group}>
            {category.id === "wrap" /*&& currentMode === "3D"*/ &&
                <CustomWrapOptionRender/>
            }
            {group && optionsSubcategories.map((o, index) => 
                
                !o.topItem[1].hidden && <OptionRender key={o.topItem[0] + "-" + index}
                    groupId={category.group}
                    optionId={o.topItem[0]}
                    option={o.topItem[1]}
                    shape={category.shape}
                    selection={group.selectionMode}
                    variants={o.items.length ? o.items.length : undefined}
                    onClick={o.openOptions && o.items.length > 0 || o.needAddFirst ? () =>
                    {
                        if (focusedItem?.optionGroup != category.group || focusedItem.optionId != o.topItem[0])
                            dispatch(VisualizerSlice.actions.focusOption({ optionGroup: category.group, optionId: o.topItem[0] }));

                        if (!selection[category.group].includes(o.topItem[0]) && getInitialUrlArgs().itemsAutoApply)
                            dispatch(optionToggle({ group: category.group, option: o.topItem[0] }));

                        if (o.openOptions && o.items.length > 0)
                            onShowVariants(o.id);
                    } : undefined} />
            )}
            {category.id !== "body_style" && 
                <ComingSoonOptionRender shape={"rectangular"/*category.shape*/}/>
            }
        </OptionsList>
        <Footer>
            <ShrinkText>
                {focusedOption ?
                    ((focusedItem?.option.buildSessionOption || focusedItem?.option.buildSessionUpdate) ?
                        <>
                            <FooterCTA 
                            testId="item-details" 
                            label={intl.formatMessage({id: "configuration.view_item_details", defaultMessage: "View Item Details"})}
                            onClick={() => itemDetailsDialog.open()}>
                                <Icon icon="Info"/>&nbsp;<FormattedMessage {...focusedOption.displayName} />
                            </FooterCTA>
                            {focusedSubcategory && focusedSubcategory.items.length > 0 && <FormattedMessage id="configurator.footer.variants" defaultMessage="{count, plural, one {# style} other {# styles} }" values={{ count: focusedSubcategory.items.length }} />}
                        </>
                        :
                        <FormattedMessage {...focusedOption.displayName} />
                    )
                    :
                    <PlaceholderMessage>
                        <FormattedMessage {...category.nothingSelectedMessage}/>
                    </PlaceholderMessage>
                }
            </ShrinkText>
            <ButtonsGroup>
                {focusedItem && focusedOption?.buildSessionOption === false &&
                    <>
                        {<IconButton label={intl.formatMessage({ id: "configuration.delete_item", defaultMessage: "DELETE" })} testId="configuration.delete" onClick={removeWrap}><Icon icon="Trash"/></IconButton>}
                        {focusedItemSelected && <SecondaryCtaButton label={intl.formatMessage({ id: "configuration.edit_item", defaultMessage: "EDIT" })} testId="configuration.edit" onClick={editWrap} />}
                    </>
                }
                {!autoItemApply && focusedItem && focusedOption &&
                    (focusedSubcategory?.items.length == 0 || (focusedSubcategory?.needAddFirst && !focusedItemSelected)) && (focusedItemSelected
                        ? <QuaternaryCtaButton label={intl.formatMessage({id: "configuration.remove_item", defaultMessage: "REMOVE"})} testId="configuration.remove" onClick={toggleItem}/>
                        : <SecondaryCtaButton label={intl.formatMessage({id: "configuration.add_item", defaultMessage: "ADD"})} testId="configuration.add" onClick={toggleItem}/>
                    )
                }
                {focusedSubcategory && (!focusedSubcategory?.needAddFirst || focusedItemSelected) && focusedSubcategory.items.length > 0 && <SecondaryCtaButton label={intl.formatMessage({ id: "configuration.pick_style", defaultMessage: "Pick style" })} testId="configuration.options" onClick={() => onShowVariants(focusedSubcategory.id)} />}
            </ButtonsGroup>
        </Footer>
        <Modal>
            <InvertTheme>
                {focusedSubcategory && <SecondLevelOptionsStyle ref={secondaryLevelMenuRef} $show={!!showVariants} onClick={(e) => { e.stopPropagation() }} onPointerDown={(e) => e.stopPropagation()}>
                    <CloseButton id="close" label={intl.formatMessage({ id: "general.close", defaultMessage: "Close" })} onClick={() =>
                    {
                        if (focusedSubcategory?.topItem && focusedItem?.optionId != focusedSubcategory.topItem[0])
                            dispatch(VisualizerSlice.actions.focusOption({ optionGroup: category.group, optionId: focusedSubcategory.topItem[0] }))
                        onShowVariants(undefined);
                    }
                    }>
                        <Icon icon="Close" color="#FF4F00"/>
                    </CloseButton>
                    <Space/>
                    <OptionsList>
                        {!focusedSubcategory.items[0]?.[1]?.subcategories?.includes("floor_and_pedals") &&
                            <NoneOptionRender groupId={category.group} variantIds={focusedSubcategory.items.map(([id]) => id)} shape={category.shape} />
                        }
                        {category.group == "wheels" && <span />}
                        {focusedSubcategory.items.map(([id, option], index, array) =>
                            <>
                                {array[index - 1]?.[1]?.characteristic != option.characteristic && ((!(option.subcategories?.includes("wheel20") && option.characteristic == "tire") && option.characteristic != "wheel" && category.group === "wheels") || (option.subcategories?.includes("floor_and_pedals"))) &&
                                    <NoneOptionRender groupId={category.group} variantIds={array.filter(([, o]) => o.characteristic == option.characteristic).map(([id]) => id)}  shape={category.shape} thumbnail={noneOptions[option.characteristic!]}/>
                                }
                                {option && <OptionRender key={id} groupId={category.group} optionId={id} option={option} shape={category.shape} selection={category.group === "body_style" ? "multiple" : "single"} />}
                                {array[index + 1]?.[1]?.characteristic != option.characteristic && <span/>}
                            </>
                        )}
                    </OptionsList>
                    <Footer>
                        <ShrinkText>
                            {focusedOption ?
                                ((focusedItem?.option.buildSessionOption || focusedItem?.option.buildSessionUpdate) ?
                                    <>
                                        <FooterCTA 
                                        testId="item-details" 
                                        label={intl.formatMessage({id: "configuration.view_item_details", defaultMessage: "View Item Details"})}
                                        onClick={() => itemDetailsDialog.open()}>
                                            <Icon icon="Info"/>&nbsp;<FormattedMessage {...focusedOption.displayName} />
                                        </FooterCTA>
                                    </>
                                    :
                                    <FormattedMessage {...focusedOption.displayName} />
                                )
                                :
                                <PlaceholderMessage>
                                    <FormattedMessage {...category.nothingSelectedMessage}/>
                                </PlaceholderMessage>
                            }
                        </ShrinkText>
                        {!autoItemApply && focusedItem && focusedOption &&
                            (focusedItemSelected
                                ? <SecondaryCtaButton label={intl.formatMessage({id: "configuration.remove_item", defaultMessage: "REMOVE"})} testId="configuration.remove" onClick={toggleItem}/>
                                : <PrimaryCtaButton label={intl.formatMessage({id: "configuration.add_item", defaultMessage: "ADD"})} testId="configuration.add" onClick={toggleItem}/>
                            )
                        }
                    </Footer>
                </SecondLevelOptionsStyle>}
                
            </InvertTheme>
        </Modal>
        {itemDetailsDialog.element}
    </>
}

// TODO Remove this mess once we have a proper subgroups data loaded from build session
const subgroupsData:Record<string, Partial<OptionData>> = {
    suspension: {
        thumbnail: { type: "image", url: "data:image/svg+xml,%3Csvg width='90' height='90' viewBox='0 0 90 90' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='90' height='90' fill='%23262626'/%3E%3Ccircle cx='20.5449' cy='69.6109' r='6' transform='rotate(45 20.5449 69.6109)' stroke='%23F9F9F9' stroke-width='2'/%3E%3Ccircle cx='20.5449' cy='69.6109' r='2' transform='rotate(45 20.5449 69.6109)' stroke='%23F9F9F9' stroke-width='2'/%3E%3Ccircle cx='69.3353' cy='20.8205' r='2' transform='rotate(45 69.3353 20.8205)' stroke='%23F9F9F9' stroke-width='2'/%3E%3Crect x='58.0215' y='17.9921' width='20' height='4' rx='2' transform='rotate(45 58.0215 17.9921)' stroke='%23F9F9F9' stroke-width='2'/%3E%3Cpath d='M27.0757 47.0359C27.328 46.3511 28.2051 46.1584 28.7211 46.6745L43.4814 61.4348C43.9974 61.9508 43.8048 62.8279 43.12 63.0802L38.9861 64.6032C38.6201 64.738 38.2091 64.6478 37.9333 64.372L25.7839 52.2225C25.5081 51.9467 25.4178 51.5357 25.5527 51.1697L27.0757 47.0359Z' stroke='%23F9F9F9' stroke-width='2'/%3E%3Cpath d='M70.0424 30.0129L74.2924 23.5369C75.7335 21.341 75.4351 18.4351 73.5779 16.5779V16.5779C71.7207 14.7207 68.8148 14.4223 66.6189 15.8634L60.1429 20.1134' stroke='%23F9F9F9' stroke-width='2'/%3E%3Crect x='45.0595' y='27.9404' width='24.4794' height='5.03825' rx='2.51913' transform='rotate(15 45.0595 27.9404)' stroke='%23F9F9F9' stroke-width='2'/%3E%3Crect x='37.9605' y='35.0396' width='24.4794' height='5.03825' rx='2.51913' transform='rotate(15 37.9605 35.0396)' stroke='%23F9F9F9' stroke-width='2'/%3E%3Crect x='30.8895' y='42.1106' width='24.4794' height='5.03825' rx='2.51913' transform='rotate(15 30.8895 42.1106)' stroke='%23F9F9F9' stroke-width='2'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M58.0215 22.2347L58.7287 22.9418L67.2139 31.4271L67.921 32.1342L67.2139 32.8413L66.3947 33.6606C66.3542 33.6475 66.3131 33.6354 66.2715 33.6242L64.1666 33.0602L65.0926 32.1342L58.0215 25.0632L53.0131 30.0716L50.7824 29.4739L57.3144 22.9418L58.0215 22.2347ZM46.6687 33.5876L43.6833 36.573L45.914 37.1707L48.8994 34.1853L46.6687 33.5876ZM60.0529 37.1739L57.0676 40.1593L59.1724 40.7232C59.214 40.7344 59.2551 40.7465 59.2957 40.7596L62.2836 37.7716L60.0529 37.1739ZM55.1846 44.8707L52.9539 44.273L49.9965 47.2303L52.1014 47.7943C52.143 47.8055 52.1841 47.8176 52.2246 47.8307L55.1846 44.8707ZM48.1135 51.9418L45.8828 51.344L39.6368 57.5901L41.051 59.0043L48.1135 51.9418ZM36.8083 63.2469L35.3941 61.8327L33.2728 63.954L26.2017 56.883L28.3231 54.7617L26.9088 53.3474L24.0804 56.1759L24.0228 56.2335L23.9752 56.2998L18.5465 63.8616C18.5177 63.9016 18.4896 63.9419 18.462 63.9825C19.3295 63.6621 20.2562 63.5489 21.1655 63.6429L24.9131 58.4228L31.733 65.2427L26.5129 68.9903C26.6069 69.8996 26.4937 70.8263 26.1733 71.6937C26.2139 71.6662 26.2542 71.6381 26.2942 71.6093L33.856 66.1806L33.9222 66.133L33.9799 66.0754L36.8083 63.2469ZM31.1515 49.1048L32.5657 50.519L34.7293 48.3555L32.4986 47.7577L31.1515 49.1048ZM36.6123 43.644L38.843 44.2417L41.8003 41.2844L39.5696 40.6867L36.6123 43.644Z' fill='%23F9F9F9'/%3E%3C/svg%3E%0A" },
    }
}


function useGroupedOptions(groupId: string, options: Record<string, OptionData>)
{
    const selected = useAppSelector(s => s.configuration.selectedOptions[groupId]);

    let groups = groupBy(Object.entries(options), ([, option]) => option.subcategories ?? []);
    if (Object.keys(groups).length <= 1)
    {
        groups = groupBy(Object.entries(options), ([id,]) => [id]);
    }
    if (groupId === "wheels")
    {
        return Object.entries(groups).map(([id, group]) => ({id, topItem: group.find(g => selected.includes(g[0]) && g[1].characteristic == "wheel") ?? group.find(g => g[1].characteristic == "wheel") ?? [group[0][0], id in subgroupsData ? {...group[0][1], ...subgroupsData[id]} : group[0][1]], items: group, openOptions: true, needAddFirst: false }));
    }
    return Object.entries(groups).map(([id, group]) => ({ id, topItem: group.find(g => selected.includes(g[0])) ?? [group[0][0], id in subgroupsData ? {...group[0][1], ...subgroupsData[id]} : group[0][1]], items: group.length > 1 ? group : [], openOptions: true, needAddFirst: false }));
}

function groupBy<TItem, TGroupKey extends string>(array: TItem[], groupPredicate: (item: TItem) => TGroupKey[])
{
    const result:Record<TGroupKey, TItem[]> = {} as never;
    for (const item of array)
    {
        const groups = groupPredicate(item);
        for (const group of groups)
        {
            result[group] = result[group] ?? []
            result[group].push(item);
        }
    }
    return result;
}

function NoneOptionRender(props: { groupId: string, variantIds: string[], shape?: "square"|"rectangular", thumbnail?: ImageMedia}) {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const thumbnailMedia = useImageMedia(props.thumbnail);
    
    const selected = useAppSelector(s => props.variantIds.find(v => s.configuration.selectedOptions[props.groupId].includes(v)));
    const focusedItem = useAppSelector(s => s.configuration.focusedOption, JSONEquality);

    
    const onClick = () => {
        if(selected) {
            //ev.currentTarget.scrollIntoView({ behavior: "smooth", inline: "nearest", block: "nearest" });
            dispatch(deselectOptions({ group: props.groupId, options: props.variantIds }));
            //dispatch(optionClick({ group: props.groupId, option: selected }));
            dispatch(VisualizerSlice.actions.focusOption({ optionGroup: focusedItem?.optionGroup ?? "", optionId: focusedItem?.optionId ?? "" }));
        }
    }

    return <OptionItem
        shape={props.shape ?? "square"}
        activeCheckShape="round"
        active={!selected}
        focused={false}
        label={intl.formatMessage({id: "none", defaultMessage: "None"})}
        thumbnail={props.thumbnail && thumbnailMedia.url ? thumbnailMedia : <Icon icon="None"/>}
        onClick={onClick}
        testId={props.groupId + "/none"}
    />
}


function OptionRender(props: { groupId:string, optionId: string, option: OptionData, shape?: "square"|"rectangular", selection: "single"|"multiple", variants?: number, onClick?: () => void })
{
    const intl = useIntl();
    const dispatch = useAppDispatch();

    const selected = useAppSelector(s => s.configuration.selectedOptions[props.groupId].includes(props.optionId));
    const focusedItem = useAppSelector(s => s.configuration.focusedOption);
    const focused = focusedItem && focusedItem.optionGroup == props.groupId && focusedItem.optionId == props.optionId;
    const thumbnailMedia = useImageMedia(props.option.thumbnail);

    const onClick = () =>
    {
        //ev.currentTarget.scrollIntoView({ behavior: "smooth", inline: "nearest", block: "nearest" });
        dispatch(optionClick({ group: props.groupId, option: props.optionId }));
    }

    let thumbnail = undefined
    if(thumbnailMedia.type === "swatch") {
        thumbnail = <CustomWrapOption colour={thumbnailMedia.colour} finish={thumbnailMedia.finish} custom={thumbnailMedia.custom}/>
    }
    else {
        thumbnail = thumbnailMedia
    }
    return <OptionItem
        shape={props.shape ?? "square"}
        activeCheckShape={props.selection === "multiple"  ? "square" : "round"}
        active={selected}
        focused={focused}
        label={intl.formatMessage(props.option.displayName)}
        name={props.option.hoverName && intl.formatMessage(props.option.hoverName)}
        thumbnail={thumbnail}
        optionsCount={props.variants}
        onClick={props.onClick ?? onClick}
        testId={props.groupId + "/" + props.optionId}
    />
}

const CustomWrapOptionItem = styled(OptionItem)`
    &:disabled {
        cursor: wait;
    }
`

function CustomWrapOptionRender() {
    const intl = useIntl()
    const {allow2DCustomWraps, allowCustomWrapUpgrade, min3DConfigChanges} = getInitialUrlArgs()
    const customColourPanel = useCustomColourPanel()

    const is2D = useAppSelector(s => s.configuration.currentMode) !== "3D"
    const is3DAllowed = Is3DAllowed(["2D", "3D"], min3DConfigChanges)
    const upgradeRequested = useAppSelector(s => s.configuration.pendingColourPicker)
    const hidden = is2D && !allow2DCustomWraps && (!is3DAllowed || !allowCustomWrapUpgrade)
    const disabled = hidden || (is2D && allowCustomWrapUpgrade && upgradeRequested);
    const currentCustomWrap = useAppSelector(s => s.configuration.currentCustomWrap);
    const dispatch = useAppDispatch()

    useEffect(() => {
        if(disabled) {
            customColourPanel.close()
        }
    }, [customColourPanel, disabled])

    useEffect(() => {
        if(currentCustomWrap && !disabled) {
            customColourPanel.open()
        }
    }, [currentCustomWrap, disabled])

    const open = useCallback(() => {
        if (is2D && !allow2DCustomWraps) {
            dispatch(VisualizerSlice.actions.setChanges(min3DConfigChanges))
            dispatch(VisualizerSlice.actions.setPendingColourPicker(true))
        }
        else {
            customColourPanel.open()
        }
    }, [allow2DCustomWraps, customColourPanel, dispatch, is2D, min3DConfigChanges])

    useEffect(() => {
        if(upgradeRequested && !is2D) {
            open()
        }
    })

    return (
        <>
            {
                !hidden &&
                <CustomWrapOptionItem
                    shape="square"
                    label={intl.formatMessage({id: "configuration.new_custom_wrap", defaultMessage: "New Custom Wrap"})}
                    thumbnail={
                        <>
                            <CustomWrapOption colour="" finish="satin" custom={false}/>
                            {allowCustomWrapUpgrade && <LoadingOverlay busy={upgradeRequested}/>}
                        </>
                    }
                    onClick={open}
                    testId="wraps/new_custom_wrap"
                    disabled={disabled}
                />
            }
            {customColourPanel.element}
        </>
    )
        
}

const Silhouette = styled.img`
    background-color: ${({theme}) => theme.colours.secondary.hover} !important;
    object-fit: contain !important;
`
const Message = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px;
    color: ${({theme}) => theme.colours.text.secondary};

`

const ComingSoonOptionItem = styled(OptionItem)`
    cursor: unset !important;
`
function ComingSoonOptionRender(props: {shape?: "square"|"rectangular"}) {
    const intl = useIntl()

    const message = intl.formatMessage({id: "item.coming_soon", defaultMessage: "Coming Soon"})
    return (
        <ComingSoonOptionItem
            shape={props.shape ?? "square"}
            label={message}
            thumbnail={
                <>
                    <Silhouette src={SilhouetteImage}/>
                    <Message>{message}</Message>
                </>
            }
            testId="coming-soon"
            disabled
            style={{cursor: "none"}}
        />
    )
        
}